/* eslint semi: 1 */
import { ref, computed, watch } from 'vue';
import { useMediaQuery } from '@/composables/media/use-media-query';
import type { MediaQueryState } from '@/pinia/stores/media/mediaQueryStore';
import type CurrentUserInfo from '@/globals/current-user-info.js';
import CONST from '@/globals/constants';

interface FilterOption {
  label: string;
  i18n: string;
  value: string | boolean | null;
  icon: string;
}

interface FilterGroupBase {
  key: string;
  label: string;
  i18n: string;
  type: 'multi-select' | 'single-select' | 'date-range';
  options: FilterOption[];
  activeFiltersCount?: number; // computed property for UI logic (number of active filters in this group, used for badges and trigger button state)
}

interface MultiSelectFilterGroup extends FilterGroupBase {
  type: 'multi-select';
  value: (string | boolean | null)[];
  explicitClear?: boolean; // tracks when the user explicitly unchecks all options
  isAllSelected?: boolean; // computed property for UI logic (true when all options are selected, either explicitly or implicitly)
  effectiveValue?: (string | boolean | null)[]; // computed property for UI logic (the actual values to apply, accounting for implicit "all selected" state)
}

interface SingleSelectFilterGroup extends FilterGroupBase {
  type: 'single-select';
  value: string | boolean | null;
}

interface DateRangeFilterGroup extends FilterGroupBase {
  type: 'date-range';
  value: { from: string | null; to: string | null };
}

type FilterGroup = MultiSelectFilterGroup | SingleSelectFilterGroup | DateRangeFilterGroup;

/**
 * UI-layer variants of each group type where the computed display properties
 * (`activeFiltersCount`, `isAllSelected`, `effectiveValue`) are required, not
 * optional. Returned by `uiSchema`.
 */
interface UIMultiSelectFilterGroup extends MultiSelectFilterGroup {
  activeFiltersCount: number;
  isAllSelected: boolean;
  effectiveValue: (string | boolean | null)[];
}

interface UISingleSelectFilterGroup extends SingleSelectFilterGroup {
  activeFiltersCount: number;
}

interface UIDateRangeFilterGroup extends DateRangeFilterGroup {
  activeFiltersCount: number;
}

type UIFilterGroup = UIMultiSelectFilterGroup | UISingleSelectFilterGroup | UIDateRangeFilterGroup;

/**
 * Typed snapshot returned by `toFilterValue()`, matching the shape
 * expected by `query.applyFilterPanelParams()`.
 */
interface FilterValues {
  fileType: MediaQueryState['mediaTypes'];
  isScheduled: MediaQueryState['isScheduled'];
  modifiedDate: {
    from: MediaQueryState['modifiedAtFrom'];
    to: MediaQueryState['modifiedAtTo']
  };
}
/**
 * File-type options for the multi-select group.
 * Maps to the `?type=` API param (`components["parameters"]["type"]`).
 */
export const FILE_TYPE_OPTIONS: FilterOption[] = [
  {
    label: 'Images',
    i18n: 'label.images',
    value: CONST.MEDIA_TYPE_VALUE_IMAGE,
    icon: 'flowbite:image-outline'
  },
  {
    label: 'Videos',
    i18n: 'label.videos',
    value: CONST.MEDIA_TYPE_VALUE_VIDEO,
    icon: 'flowbite:video-camera-outline'
  },
  {
    label: 'Audio',
    i18n: 'planCard.audio',
    value: CONST.MEDIA_TYPE_VALUE_AUDIO,
    icon: 'flowbite:music-outline'
  },
  {
    label: 'Audio Playlist',
    i18n: 'label.audioPlaylist',
    value: CONST.MEDIA_TYPE_VALUE_AUDIO_PLAYLIST,
    icon: 'flowbite:list-music-outline'
  },
  {
    label: 'Projects/PDF',
    i18n: 'label.projectsOrPdf',
    value: CONST.MEDIA_TYPE_VALUE_PROJECT,
    icon: 'flowbite:file-pen-outline'
  },
  {
    label: 'Elementi',
    i18n: 'label.elementiProject',
    value: CONST.MEDIA_TYPE_VALUE_ELEMENTI,
    icon: 'flowbite:file-code-outline'
  }
];

/**
 * Same with {@link FILE_TYPE_OPTIONS} but renamed for the alerts page
 */
export const ALERT_FILE_TYPE_OPTIONS: FilterOption[] = [
  {
    label: 'Images',
    i18n: 'label.images',
    value: CONST.MEDIA_TYPE_VALUE_IMAGE,
    icon: 'flowbite:image-outline'
  },
  {
    label: 'Videos',
    i18n: 'label.videos',
    value: CONST.MEDIA_TYPE_VALUE_VIDEO,
    icon: 'flowbite:video-camera-outline'
  },
  {
    label: 'Projects/PDF',
    i18n: 'label.projectsOrPdf',
    value: CONST.MEDIA_TYPE_VALUE_PROJECT,
    icon: 'flowbite:file-pen-outline'
  },
  {
    label: 'Elementi',
    i18n: 'label.elementiProject',
    value: CONST.MEDIA_TYPE_VALUE_ELEMENTI,
    icon: 'flowbite:file-code-outline'
  }
];

/**
 * Content-status options for the single-select group.
 * Maps to the `?scheduled=` API param (`components["parameters"]["scheduled"]`).
 * `value: null` = no filter (param omitted)
 * `value: true` = ?scheduled=true
 * `value: false` = ?scheduled=false
 */
export const IS_SCHEDULED_OPTIONS: FilterOption[] = [
  {
    label: 'All',
    i18n: 'label.all',
    value: null,
    icon: 'flowbite:grid-outline'
  },
  {
    label: 'Scheduled',
    i18n: 'label.scheduled',
    value: true,
    icon: 'flowbite:calendar-outline'
  },
  {
    label: 'Not Scheduled',
    i18n: 'label.notScheduled',
    value: false,
    icon: 'flowbite:calendar-clear-outline'
  }
];

/**
 * Same with {@link IS_SCHEDULED_OPTIONS} but renamed for the alerts page
 * usage.
 */
export const IS_ALERT_ACTIVE_OPTIONS: FilterOption[] = [
  {
    label: 'All',
    i18n: 'label.all',
    value: null,
    icon: 'flowbite:grid-outline'
  },
  {
    label: 'Active',
    i18n: 'label.active',
    value: true,
    icon: 'flowbite:calendar-outline'
  },
  {
    label: 'Inactive',
    i18n: 'label.inactive',
    value: false,
    icon: 'flowbite:calendar-clear-outline'
  }
];

/**
 * Immutable default schema — the source of truth for group definitions and
 * default values. Never mutate directly; use `useMediaFiltering()` to get a
 * reactive working copy.
 */
export const MEDIA_FILTER_DEFAULT_SCHEMA: readonly FilterGroup[] = Object.freeze([
  {
    key: 'fileType',
    label: 'File Types',
    i18n: 'label.fileTypes',
    type: 'multi-select',
    options: FILE_TYPE_OPTIONS,
    value: []
  },
  {
    key: 'isScheduled',
    label: 'Content Status',
    i18n: 'label.contentStatus',
    type: 'single-select',
    options: IS_SCHEDULED_OPTIONS,
    value: null
  },
  {
    key: 'modifiedDate',
    label: 'Modified Date',
    i18n: 'label.modifiedDate',
    type: 'date-range',
    options: [],
    value: { from: null, to: null }
  }
]);

/**
 * Same with {@link MEDIA_FILTER_DEFAULT_SCHEMA} but renamed for the alerts page usage.
 */
export const ALERTS_FILTER_DEFAULT_SCHEMA: readonly FilterGroup[] = Object.freeze([
  {
    key: 'fileType',
    label: 'File Types',
    i18n: 'label.fileTypes',
    type: 'multi-select',
    options: ALERT_FILE_TYPE_OPTIONS,
    value: []
  },
  {
    key: 'isScheduled',
    label: 'Is Alert Active',
    i18n: 'label.isAlertActive',
    type: 'single-select',
    options: IS_ALERT_ACTIVE_OPTIONS,
    value: null
  },
  {
    key: 'modifiedDate',
    label: 'Modified Date',
    i18n: 'label.modifiedDate',
    type: 'date-range',
    options: [],
    value: { from: null, to: null }
  }
]);

/**
 * Composable for the `MediaFilter` dropdown component.
 *
 * Manages the **local/draft** state of the filter panel — the values the user
 * is currently configuring before they click "Apply". This
 * composable bridges to `useMediaQuery` for persistence
 * (URL params + localStorage) and API param generation.
 */
export function useMediaFiltering (currentUser?: typeof CurrentUserInfo) {
  const query = useMediaQuery();
  const schema = ref<FilterGroup[]>(buildSchema());

  // Keep the draft schema in sync with committed query state.
  watch(
    [query.mediaTypes, query.isScheduled, query.modifiedAtFrom, query.modifiedAtTo],
    () => {
      initialize();
    },
    { immediate: true, deep: true }
  );

  /**
   * UI Schema with computed properties for template logic.
   * Populates properties like:
   * - `isAllSelected` for multi-select groups
   * - `activeFiltersCount` for all groups (used for badges and trigger button state)
   */
  const uiSchema = computed((): UIFilterGroup[] => schema.value.map((group): UIFilterGroup => {
    if (group.type === 'multi-select') {
      const implicitAll = group.value.length === 0 && !group.explicitClear;
      const activeFiltersCount = group.value.length;
      return {
        ...group,
        activeFiltersCount,
        isAllSelected: implicitAll || (group.options.length > 0 && activeFiltersCount === group.options.length),
        effectiveValue: implicitAll ? group.options.map(o => o.value) : group.value
      };
    } else if (group.type === 'single-select') {
      return {
        ...group,
        activeFiltersCount: group.value !== null && group.value !== undefined ? 1 : 0
      };
    } else {
      return {
        ...group,
        activeFiltersCount: group.value.from !== null && group.value.to !== null ? 1 : 0
      };
    }
  }));

  /**
   * True when at least one group has a non-default value.
   */
  const hasActiveFilters = computed(() =>
    schema.value.filter((group) => {
      if (group.type === 'multi-select') return group.value.length > 0;
      if (group.type === 'single-select') return group.value !== null && group.value !== undefined;
      if (group.type === 'date-range') return group.value.from !== null || group.value.to !== null;
      return false;
    }).length > 0
  );

  /**
   * Number of filter groups that have been **committed**
   * (i.e. persisted via Apply). Reads directly from `query`,
   * not the draft schema, so it only updates when the user
   * clicks Apply or Clear All.
   */
  const committedFiltersCount = computed(() => {
    let count = 0;
    if (query.mediaTypes.value.length > 0) count += query.mediaTypes.value.length;
    if (query.isScheduled.value !== null && query.isScheduled.value !== undefined) count++;
    if (query.modifiedAtFrom.value !== null || query.modifiedAtTo.value !== null) count++;
    return count;
  });

  /** True when `committedFiltersCount > 0`. */
  const hasCommittedFilters = computed(() => committedFiltersCount.value > 0);

  /**
   * Replaces the value for a single-select or date-range group.
   * For multi-select groups use `toggleMultiSelectOption` instead.
   */
  function setGroupValue (key: string, value: string | boolean | null) {
    const group = schema.value.find(g => g.key === key);
    if (group) group.value = value;
  }

  /**
   * Toggles an individual option inside a multi-select group.
   * Adds the option if not currently selected; removes it if already selected.
   */
  function toggleMultiSelectOption (key: string, optionValue: string | boolean | null) {
    const group = schema.value.find((g): g is MultiSelectFilterGroup => g.key === key && g.type === 'multi-select');
    if (!group) return;

    // When in "implicit all selected" state (value=[], not explicitly cleared),
    // expand to all options except the one being deselected (req 3).
    if (group.value.length === 0 && !group.explicitClear) {
      group.value = group.options.map(o => o.value).filter(v => v !== optionValue);
      return;
    }

    const idx = group.value.indexOf(optionValue);
    const newValue = idx === -1
      ? [...group.value, optionValue]
      : group.value.filter(v => v !== optionValue);
    // When the last item is unchecked, mark as explicitly cleared so the
    // UI shows nothing checked rather than snapping back to "all selected".
    if (newValue.length === 0) {
      group.value = newValue;
      group.explicitClear = true;
    } else if (newValue.length === group.options.length) {
      // All options manually selected → treat as implicit all (no filter applied),
      // same behaviour as toggling the "All" checkbox.
      group.value = [];
      group.explicitClear = false;
    } else {
      group.value = newValue;
    }
  }

  /**
   * Toggles the "All" state for a multi-select group.
   * - All selected (implicit or explicit) → explicitly cleared (nothing checked, no filter)
   * - Not all selected → reset to implicit all (value=[], explicitClear=false, no filter)
   * @param {string} key
   */
  function toggleAllForGroup (key: string) {
    const group = schema.value.find((g): g is MultiSelectFilterGroup => g.key === key && g.type === 'multi-select');
    if (!group) return;
    const implicitAll = group.value.length === 0 && !group.explicitClear;
    const explicitAll = group.options.length > 0 && group.value.length === group.options.length;
    if (implicitAll || explicitAll) {
      // Was all selected → toggle to explicitly none (unchecked, still no filter)
      group.value = [];
      group.explicitClear = true;
    } else {
      // Was partially or explicitly-none → reset to all selected
      group.value = [];
      group.explicitClear = false;
    }
  }

  /** Resets all groups back to their default empty values (local draft only). */
  function resetAll () {
    schema.value = buildSchema();
  }

  /**
   * Returns a flat snapshot of all current group values keyed by group key.
   * @returns {object}
   */
  function toFilterValue (): FilterValues {
    return Object.fromEntries(
      schema.value.map(g => [g.key, cloneValue(g.value)])
    ) as unknown as FilterValues;
  }

  /**
   * Returns a set of file type values that should be
   * restricted for the current user.
   * @returns {Set<string>}
   */
  function getRestrictedFileTypes (): Set<string> {
    const restrictedTypes = new Set<string>();
    if (!currentUser) return restrictedTypes;

    if (!currentUser.hasGroupExtended() || (
      !currentUser.hasGroupUserAdmin() &&
      !currentUser.hasGroupUserCreate() &&
      !currentUser.hasGroupUserCreateLock()
    )) {
      // Restrict for non extended group or non-admin, non-create, non-createLock users
      restrictedTypes.add(CONST.MEDIA_TYPE_VALUE_AUDIO);
      restrictedTypes.add(CONST.MEDIA_TYPE_VALUE_AUDIO_PLAYLIST);
    }

    if (!currentUser.hasGroupEnterprise()) {
      // Restrict for non-enterprise group users
      restrictedTypes.add(CONST.MEDIA_TYPE_VALUE_ELEMENTI);
    }

    return restrictedTypes;
  }

  /**
   * Seeds the draft schema from the current active query state.
   * Call this when the filter panel opens so it always
   * reflects saved filters.
   * @prop currentUser - optional, used to determine if the Audio Media type filter should be removed
   */
  function initialize () {
    const baseSchemaSource = query.isAlertPage.value
      ? ALERTS_FILTER_DEFAULT_SCHEMA
      : MEDIA_FILTER_DEFAULT_SCHEMA;

    const restrictedFileTypes = getRestrictedFileTypes();
    const baseSchema = restrictedFileTypes.size > 0
      ? baseSchemaSource.map((group) => {
        if (group.type !== 'multi-select' || group.key !== 'fileType') return group;
        return {
          ...group,
          options: group.options.filter((opt) => !restrictedFileTypes.has(opt.value as string))
        };
      })
      : baseSchemaSource;

    schema.value = buildSchema(baseSchema, {
      fileType: query.mediaTypes.value,
      isScheduled: query.isScheduled.value,
      modifiedDate: {
        from: query.modifiedAtFrom.value,
        to: query.modifiedAtTo.value
      }
    });
  }

  /**
   * Applies the current draft schema values to the query
   * layer (persists to `$filtersRepository` and updates
   * query `apiParams`).
   */
  function apply () {
    const value = toFilterValue();
    query.applyFilterPanelParams({
      types: value.fileType ?? [],
      scheduled: value.isScheduled ?? null,
      modifiedAtFrom: value.modifiedDate?.from ?? null,
      modifiedAtTo: value.modifiedDate?.to ?? null
    });
  }

  /**
   * Resets the draft schema to defaults AND clears the corresponding
   * filter values in the query layer.
   */
  function clearAll () {
    schema.value = buildSchema();
    query.clearFilterPanelParams();
  }

  return {
    // States
    schema,
    uiSchema,
    // Computed states
    hasActiveFilters,
    committedFiltersCount,
    hasCommittedFilters,
    // Mutations
    setGroupValue,
    toggleMultiSelectOption,
    toggleAllForGroup,
    resetAll,
    toFilterValue,
    // Query bridge
    initialize,
    apply,
    clearAll
  };
}

/**
 * Shallow-clones a schema value into a plain (non-reactive) copy.
 * All schema values are either primitives, flat arrays, or one-level objects
 * (e.g. `{ from, to }`), so a shallow copy is sufficient.
 */
function cloneValue<T> (value: T): T {
  if (Array.isArray(value)) return [...value] as unknown as T;
  if (value !== null && typeof value === 'object') return { ...value } as T;
  return value;
}

/**
 * Creates a mutable copy of `MEDIA_FILTER_DEFAULT_SCHEMA`,
 * optionally seeded with active values.
 */
function buildSchema (
  baseSchema: readonly FilterGroup[] = MEDIA_FILTER_DEFAULT_SCHEMA,
  overrides: Partial<Record<string, unknown>> = {}
): FilterGroup[] {
  return baseSchema.map((group) => {
    // explicitClear tracks when the user deliberately unchecks all items in a
    // multi-select group so the UI stays empty rather than snapping back to
    // "all selected" (which is the default implicit state for value=[]).
    const hasOverride = Object.prototype.hasOwnProperty.call(overrides, group.key);
    return {
      ...group,
      options: [...group.options],
      value: cloneValue(hasOverride ? overrides[group.key] : group.value),
      ...(group.type === 'multi-select' ? { explicitClear: false } : {})
    };
  }) as FilterGroup[];
}
